// The sim service's calls the apps share. Robot control pairs the phone before the session: the mission page shows
// the pairing QR, polls until a phone has scanned it, then starts the session with `pairing_id` (the phone joins it
// by itself). See contracts/sim-pairing.md.
import type { ApiClient } from './client.ts'
import type * as T from './types.ts'

const enc = encodeURIComponent

export function simApi(c: ApiClient) {
  return {
    pairings: {
      /** A pairing code for this browser, before any session (single use, about 10 minutes). */
      create: () => c.post<T.SimPrePairingCreated>('sim', '/v1/pairings'),
      /** Poll every 1–2 s while the QR is on screen. */
      get: (id: string) => c.get<T.SimPrePairing>('sim', `/v1/pairings/${enc(id)}`),
    },
    sessions: {
      create: <R = unknown>(body: T.SimSessionCreate) => c.post<R>('sim', '/v1/sessions', body),
    },
    /** Agent data collection (ops, sim:operate): the daily point plan, its items and the generator. */
    agentdata: {
      status: () => c.get<T.AgentDataStatus>('sim', '/v1/agentdata/status'),
      settings: (body: Partial<T.AgentDataSettings>) => c.put<T.AgentDataStatus>('sim', '/v1/agentdata/settings', body),
      days: (q: { from: string; to: string }) => c.get<{ days: T.AgentDataDay[] }>('sim', '/v1/agentdata/days', { query: q as never }),
      setDay: (day: string, body: { points_budget: number; note?: string }) => c.put<T.AgentDataDay>('sim', `/v1/agentdata/days/${enc(day)}`, body),
      replan: (day: string) => c.post<T.AgentDataDay>('sim', `/v1/agentdata/days/${enc(day)}/replan`),
      clearDay: (day: string) => c.delete<{ ok: boolean }>('sim', `/v1/agentdata/days/${enc(day)}`),
      items: (day: string, q: { agent_id?: string } = {}) =>
        c.get<{ items: T.AgentDataItem[] }>('sim', `/v1/agentdata/days/${enc(day)}/items`, { query: q as never }),
      /** Download RoboCasa's human source demos (default: approved tasks missing them). */
      fetchSources: (body: { task_ids?: string[] }) => c.post<Record<string, unknown>>('sim', '/v1/agentdata/sources/fetch', body),
    },
  }
}

export type SimApi = ReturnType<typeof simApi>
